// ════════════════════════════════════════════════════════════════════════
// InterviewPracticePage.tsx — Guion bilingüe de práctica de entrevista
// ════════════════════════════════════════════════════════════════════════
// Entrevista simulada (inglés/español) para practicar el rol de Full-Stack
// .NET + React Technical Leader (Sr.). Cada turno muestra el inglés y su
// traducción al español debajo, más un banco de frases reutilizables al
// final. Contenido estático — no consume ningún servicio, solo vive en el
// panel para tenerlo a mano y practicar antes de una entrevista real.
// ════════════════════════════════════════════════════════════════════════

import { DashboardLayout } from '../layouts/DashboardLayout/DashboardLayout';
import { AppSidebarMenu } from '../components/AppSidebarMenu/AppSidebarMenu';
import styles from './InterviewPracticePage.module.css';

type Speaker = 'recruiter' | 'candidate';

interface Turn {
  speaker: Speaker;
  name: string;
  en: string;
  es: string;
}

const SCRIPT: Turn[] = [
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: "Hi! Thanks so much for joining me today. My name is Laura, and I'm a technical recruiter at BrightPath Digital. How are you doing today?",
    es: '¡Hola! Muchas gracias por unirte hoy. Me llamo Laura y soy reclutadora técnica en BrightPath Digital. ¿Cómo estás hoy?',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: "Hi Laura, I'm doing great, thank you! I'm really excited about this conversation and about the Technical Leader position.",
    es: 'Hola Laura, estoy muy bien, ¡gracias! Estoy muy emocionado por esta conversación y por la posición de Líder Técnico.',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: 'Great to hear! Let me tell you a bit about us. BrightPath Digital is a remote-first software company. We build web platforms for clients in fintech and logistics, mostly in the US and Europe. We have about 120 people, and our engineering team is based across Latin America.',
    es: '¡Qué bueno! Déjame contarte un poco sobre nosotros. BrightPath Digital es una empresa de software totalmente remota. Construimos plataformas web para clientes de fintech y logística, principalmente en Estados Unidos y Europa. Somos alrededor de 120 personas, y nuestro equipo de ingeniería está en América Latina.',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: "The role we're hiring for is a Senior Full-Stack Technical Leader. You would work with .NET on the backend and React on the frontend. You'll lead a team of five engineers, make architecture decisions, and mentor the mid-level developers.",
    es: 'La posición que estamos buscando es un Líder Técnico Senior Full-Stack. Trabajarías con .NET en el backend y React en el frontend. Liderarías un equipo de cinco ingenieros, tomarías decisiones de arquitectura y guiarías a los desarrolladores de nivel medio.',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: "Now, let's talk about compensation. The salary range for this position is between four thousand five hundred and five thousand eight hundred dollars a month, depending on your experience. We pay monthly, in US dollars.",
    es: 'Ahora, hablemos de la compensación. El rango salarial para esta posición es entre cuatro mil quinientos y cinco mil ochocientos dólares al mes, dependiendo de tu experiencia. Pagamos mensualmente, en dólares estadounidenses.',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: "Before we go further, I'd love to hear more about you. Can you tell me about your experience with .NET and React?",
    es: 'Antes de seguir, me encantaría escuchar más sobre ti. ¿Puedes contarme sobre tu experiencia con .NET y React?',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: "Of course. I have solid experience building backend systems with .NET, and I also work with AWS for cloud infrastructure. I use Terraform to manage that infrastructure as code, and I've designed several API architectures for different projects.",
    es: 'Claro. Tengo experiencia sólida construyendo sistemas backend con .NET, y también trabajo con AWS para infraestructura en la nube. Uso Terraform para manejar esa infraestructura como código, y he diseñado varias arquitecturas de API para distintos proyectos.',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: 'That sounds great. And what about leadership? Have you managed or led a development team before?',
    es: 'Eso suena muy bien. ¿Y qué hay del liderazgo? ¿Has manejado o liderado un equipo de desarrollo antes?',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: "Yes, I have. I've led small teams before, reviewing code, planning sprints, and helping other developers grow. I really enjoy mentoring people and making technical decisions together with the team.",
    es: 'Sí, lo he hecho. He liderado equipos pequeños antes, revisando código, planeando sprints y ayudando a otros desarrolladores a crecer. Disfruto mucho guiar a otras personas y tomar decisiones técnicas junto con el equipo.',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: 'Can I ask you something about the projects? What kind of projects would I be working on if I get this role?',
    es: '¿Puedo preguntarte algo sobre los proyectos? ¿En qué tipo de proyectos trabajaría si consigo esta posición?',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: "Sure! Right now, the team is migrating a legacy monolith to a microservices architecture on AWS. The frontend is being rebuilt in React, with a modern design system. It's a long-term project with a lot of room to make technical decisions.",
    es: '¡Claro! En este momento, el equipo está migrando un monolito heredado a una arquitectura de microservicios en AWS. El frontend se está reconstruyendo en React, con un sistema de diseño moderno. Es un proyecto a largo plazo con mucho espacio para tomar decisiones técnicas.',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: 'That sounds really interesting. Can I ask a question about the salary? Is there any bonus, or is the salary fixed every month?',
    es: 'Eso suena muy interesante. ¿Puedo hacer una pregunta sobre el salario? ¿Hay algún bono, o el salario es fijo cada mes?',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: 'Good question. The base salary is fixed every month, but we also offer a yearly performance bonus. On top of that, you get paid time off, a home-office stipend, and a yearly budget for courses or certifications.',
    es: 'Buena pregunta. El salario base es fijo cada mes, pero también ofrecemos un bono anual por desempeño. Además de eso, tienes días de vacaciones pagados, un estipendio para tu oficina en casa, y un presupuesto anual para cursos o certificaciones.',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: "That's really good to know. One more question — how is the payment made? Is it a direct deposit, or do you use a platform like Deel?",
    es: 'Eso está muy bien saberlo. Una pregunta más: ¿cómo se hace el pago? ¿Es un depósito directo, o usan una plataforma como Deel?',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: "We use Deel for international payments. It's simple, and you receive the payment in your local bank account or in a digital wallet, whatever you prefer.",
    es: 'Usamos Deel para los pagos internacionales. Es sencillo, y recibes el pago en tu cuenta bancaria local o en una billetera digital, lo que prefieras.',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: 'Now, one last question for today. What is your availability? Do you need to give notice at your current job?',
    es: 'Ahora, una última pregunta por hoy. ¿Cuál es tu disponibilidad? ¿Necesitas dar aviso previo en tu trabajo actual?',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: 'I would need to give a two-week notice. And before we finish, could you tell me what the next steps in the process are?',
    es: 'Necesitaría dar un aviso de dos semanas. Y antes de terminar, ¿podrías decirme cuáles son los siguientes pasos en el proceso?',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: "Of course. The next step is a technical interview with our Engineering Manager. I'll send you an email today with the details and some available time slots.",
    es: 'Claro. El siguiente paso es una entrevista técnica con nuestro Gerente de Ingeniería. Te enviaré un correo hoy con los detalles y algunos horarios disponibles.',
  },
  {
    speaker: 'candidate',
    name: 'You — Candidate',
    en: "That sounds great, thank you so much, Laura. I really appreciate your time today, and I'm looking forward to the next step.",
    es: 'Eso suena excelente, muchas gracias, Laura. Realmente aprecio tu tiempo hoy, y espero con ganas el siguiente paso.',
  },
  {
    speaker: 'recruiter',
    name: 'Laura — Recruiter',
    en: 'Thank you too! It was a pleasure talking with you. Have a great rest of your day!',
    es: '¡Gracias a ti también! Fue un placer hablar contigo. ¡Que tengas un excelente resto de tu día!',
  },
];

interface PhraseGroup {
  title: string;
  phrases: { en: string; es: string }[];
}

const PHRASE_BANK: PhraseGroup[] = [
  {
    title: 'Salario y beneficios',
    phrases: [
      { en: "What's the salary range for this role?", es: '¿Cuál es el rango salarial para esta posición?' },
      { en: 'Is the salary negotiable?', es: '¿El salario es negociable?' },
      { en: 'Are there any bonuses?', es: '¿Hay algún tipo de bono?' },
      { en: 'What benefits do you offer?', es: '¿Qué beneficios ofrecen?' },
    ],
  },
  {
    title: 'Hablar de tu experiencia',
    phrases: [
      { en: 'I have five years of experience with .NET and React.', es: 'Tengo cinco años de experiencia con .NET y React.' },
      { en: "I'm responsible for the backend architecture and the API design.", es: 'Soy responsable de la arquitectura backend y del diseño de las APIs.' },
      { en: 'I led a team of four developers on a microservices project.', es: 'Lideré un equipo de cuatro desarrolladores en un proyecto de microservicios.' },
      { en: "I'm comfortable working with AWS, Terraform, and CI/CD pipelines.", es: 'Me siento cómodo trabajando con AWS, Terraform y pipelines de CI/CD.' },
      { en: 'In my last role, I designed the API architecture for a new product.', es: 'En mi último puesto, diseñé la arquitectura de API para un nuevo producto.' },
      { en: 'I specialize in backend development and cloud infrastructure.', es: 'Me especializo en desarrollo backend e infraestructura en la nube.' },
    ],
  },
  {
    title: 'Preguntar sobre proyectos',
    phrases: [
      { en: 'What kind of projects would I work on?', es: '¿En qué tipo de proyectos trabajaría?' },
      { en: 'What does the tech stack look like?', es: '¿Cómo es el stack tecnológico?' },
      { en: 'Is this a new project or an existing one?', es: '¿Es un proyecto nuevo o uno existente?' },
    ],
  },
  {
    title: 'Cierre y siguientes pasos',
    phrases: [
      { en: 'What are the next steps in the process?', es: '¿Cuáles son los siguientes pasos en el proceso?' },
      { en: 'When can I expect to hear back from you?', es: '¿Cuándo puedo esperar noticias de ustedes?' },
      { en: 'Thank you for your time.', es: 'Gracias por tu tiempo.' },
    ],
  },
];

export default function InterviewPracticePage() {
  return (
    <DashboardLayout.Root>
      <DashboardLayout.Sidebar>
        <AppSidebarMenu />
      </DashboardLayout.Sidebar>

      <DashboardLayout.Content>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Entrevista: Tech Lead .NET + React</h1>
          <p className={styles.pageSubtitle}>
            Guion bilingüe para practicar fluidez y confianza antes de la entrevista real.
          </p>
        </div>

        <dl className={styles.facts}>
          <div className={styles.fact}>
            <dt>Empresa</dt>
            <dd>BrightPath Digital</dd>
          </div>
          <div className={styles.fact}>
            <dt>Formato</dt>
            <dd>Videollamada · 45 min</dd>
          </div>
          <div className={styles.fact}>
            <dt>Nivel</dt>
            <dd>Inglés B1–B2</dd>
          </div>
          <div className={styles.fact}>
            <dt>Enfoque</dt>
            <dd>Salario, experiencia, proyectos</dd>
          </div>
        </dl>

        <div className={styles.howto}>
          <p>
            <strong>How to practice:</strong> Read the script out loud twice — first slowly, then at a
            natural speed, like a real call. Cover the Spanish lines with your hand and try saying the
            English lines from memory. Then switch roles and read the recruiter&apos;s lines too, so
            asking questions feels as natural as answering them.
          </p>
          <p className={styles.howtoEs}>
            <strong>Cómo practicar:</strong> Lee el guion en voz alta dos veces — primero despacio, luego
            a un ritmo natural, como en una llamada real. Cubre las líneas en español con la mano e
            intenta decir las líneas en inglés de memoria. Luego cambia de rol y lee también las líneas
            del reclutador, para que hacer preguntas se sienta tan natural como responderlas.
          </p>
        </div>

        <h2 className={styles.sectionTitle}>La entrevista</h2>
        <p className={styles.sectionNote}>
          Laura (reclutadora) y tú (candidato) — un ida y vuelta realista, con tus propias preguntas
          sobre pago y proyectos.
        </p>

        <div className={styles.legend}>
          <span>
            <i className={`${styles.dot} ${styles.dotRecruiter}`} /> Laura — Recruiter
          </span>
          <span>
            <i className={`${styles.dot} ${styles.dotCandidate}`} /> You — Candidate
          </span>
        </div>

        <div className={styles.script}>
          {SCRIPT.map((turn, i) => (
            <div
              key={i}
              className={`${styles.turn} ${turn.speaker === 'candidate' ? styles.turnCandidate : ''}`}
            >
              <p
                className={`${styles.speaker} ${
                  turn.speaker === 'candidate' ? styles.speakerCandidate : styles.speakerRecruiter
                }`}
              >
                {turn.name}
              </p>
              <p className={styles.en}>{turn.en}</p>
              <p className={styles.es}>{turn.es}</p>
            </div>
          ))}
        </div>

        <h2 className={styles.sectionTitle}>Banco de frases</h2>
        <p className={styles.sectionNote}>Frases clave que puedes reutilizar en cualquier entrevista.</p>

        <div className={styles.bankGrid}>
          {PHRASE_BANK.map((group) => (
            <div key={group.title} className={styles.bankCard}>
              <h3 className={styles.bankTitle}>{group.title}</h3>
              <dl>
                {group.phrases.map((p) => (
                  <div key={p.en} className={styles.pair}>
                    <dt className={styles.pairEn}>{p.en}</dt>
                    <dd className={styles.pairEs}>{p.es}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </DashboardLayout.Content>
    </DashboardLayout.Root>
  );
}
